import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, TitleStrategy } from '@angular/router';
import { provideAbpCore, withOptions } from '@abp/ng.core';
import { provideAbpOAuth } from '@abp/ng.oauth';
import { provideSettingManagementConfig } from '@abp/ng.setting-management/config';
import { provideFeatureManagementConfig } from '@abp/ng.feature-management';
import { provideCommercialUiConfig } from '@volo/abp.commercial.ng.ui/config';
import { provideAccountAdminConfig } from '@volo/abp.ng.account/admin/config';
import { provideAccountPublicConfig } from '@volo/abp.ng.account/public/config';
import { provideAuditLoggingConfig } from '@volo/abp.ng.audit-logging/config';
import { provideIdentityConfig } from '@volo/abp.ng.identity/config';
import { provideLanguageManagementConfig } from '@volo/abp.ng.language-management/config';
import { registerLocale } from '@volo/abp.ng.language-management/locale';
import { provideOpeniddictproConfig } from '@volo/abp.ng.openiddictpro/config';
import { provideSaasConfig } from '@volo/abp.ng.saas/config';
import { provideTextTemplateManagementConfig } from '@volo/abp.ng.text-template-management/config';
import { provideGdprConfig, withCookieConsentOptions } from '@volo/abp.ng.gdpr/config';
import { provideLogo, withEnvironmentOptions } from '@volo/ngx-lepton-x.core';
import { environment } from '../environments/environment';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { APP_ROUTES } from './app.routes';
import { ThemeLeptonXModule, HttpErrorComponent, provideAbpValidationError } from '@volosoft/abp.ng.theme.lepton-x';
import { SideMenuLayoutModule } from '@volosoft/abp.ng.theme.lepton-x/layouts';
import { ThemeSharedModule, withHttpErrorConfig, withValidationBluePrint, provideAbpThemeShared } from '@abp/ng.theme.shared';
import { provideSguApplicationLayout } from './layout/sgu-layout.provider';
import { provideSguRoutesComponent } from './core/sgu-routes/sgu-routes.provider';
import { provideUserMenuFilter } from './core/user-menu-filter.service';
import { provideUserDisplayName } from './core/user-display-name.service';
import { provideFacultyManagementPendingApprovalCount } from './core/faculty-management-pending-approval-count.service';
import { AppPageTitleStrategy } from './core/page-title.strategy';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(APP_ROUTES),
    APP_ROUTE_PROVIDER,
    provideAnimations(),
    provideAbpCore(
      withOptions({
        environment,
        registerLocaleFn: registerLocale(),
      })
    ),
    provideAbpOAuth(),
    provideAccountAdminConfig(),
    provideAccountPublicConfig(),
    provideIdentityConfig(),
    provideLanguageManagementConfig(),
    provideSaasConfig(),
    provideAuditLoggingConfig(),
    provideOpeniddictproConfig(),
    provideTextTemplateManagementConfig(),
    provideSettingManagementConfig(),
    provideCommercialUiConfig(),
    provideFeatureManagementConfig(),
    provideGdprConfig(
      withCookieConsentOptions({
        privacyPolicyUrl: 'gdpr-cookie-consent/privacy',
        cookiePolicyUrl: 'gdpr-cookie-consent/cookie',
      })
    ),
    provideLogo(withEnvironmentOptions(environment)),
    provideAbpValidationError(),
    importProvidersFrom(
     ThemeLeptonXModule.forRoot({
        defaultTheme: 'light',
        themeOptions: {
          localStorageKey: 'LPX_THEME',      // keep the default key
          disableSystemOption: true,         // no “System” option
          styleFactory: (themes) =>
            themes.filter(t => t.styleName === 'light'), // only keep light
        },
      }),
      SideMenuLayoutModule.forRoot(),
      ThemeSharedModule
    ),
    provideSguApplicationLayout(),
    provideSguRoutesComponent(),
    provideAbpThemeShared(
      withValidationBluePrint({
        wrongPassword: 'Please choose 1q2w3E*',
        phonePattern: 'Invalid phone number',
      }),
      withHttpErrorConfig({
        errorScreen: {
          component: HttpErrorComponent,
          forWhichErrors: [401, 403, 404, 500],
          hideCloseIcon: true,
        },
      })
    ),
    provideUserMenuFilter(),
    provideUserDisplayName(),
    provideFacultyManagementPendingApprovalCount(),
    {
      provide: TitleStrategy,
      useClass: AppPageTitleStrategy,
    },
  ],
};
